/**
 * TutorialContext — the running tutorial.
 *
 * Everything stateful lives here so the step definitions can stay pure data:
 * which step we are on, which ids the tutorial has learned, where its holes are,
 * what is clickable, and what to do when the app says something happened.
 *
 * The provider sits inside the router (it watches and drives the location) and
 * above the routes (a tutorial outlives any one page).
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import AuthContext from '../../context/AuthContext';
import useIsMobile from '../../utils/useIsMobile';
import { updateMyTutorialStatus } from '../../services/UserServices';
import registry from '../registry';
import tutorialBus from './tutorialBus';
import tutorialReducer, { initialState, TUTORIAL_STATUS } from './tutorialReducer';
import { clearSession, readSession, writeSession } from './session';
import { normalizeStep } from './stepSchema';
import { toSelector, waitForElements } from './resolveTargets';
import useSpotlightRects from './useSpotlightRects';
import { installShield } from './useClickShield';

/** How long a route can mismatch before the tutorial steps aside. */
const PAUSE_DEBOUNCE_MS = 500;

/**
 * Used when no provider is mounted — component tests render editor pieces in
 * isolation and must not have to know the tutorial exists.
 */
const INERT = Object.freeze({
    status: TUTORIAL_STATUS.IDLE,
    isRunning: false,
    isActive: false,
    isPaused: false,
    isFinished: false,
    tutorial: null,
    step: null,
    stepIndex: 0,
    stepCount: 0,
    entities: {},
    hint: null,
    resolveError: false,
    ctx: { entities: {} },
    hasBeenPrompted: () => true,
    start: () => {},
    exit: () => {},
    next: () => {},
    retry: () => {},
    resume: () => {},
    isLocked: () => false,
    registerActions: () => () => {},
    markPrompted: () => Promise.resolve(),
    markCompleted: () => Promise.resolve(),
    markDismissed: () => Promise.resolve(),
    dismissComplete: () => {},
    rects: [],
    anchorEl: null,
});

const TutorialContext = createContext(INERT);

/** Fills ':scenarioId' so a pattern can be matched against a concrete path. */
export function fillRoute(pattern, entities) {
    if (!pattern) return pattern;
    return entities?.scenarioId ? pattern.replace(/:scenarioId/g, entities.scenarioId) : pattern;
}

export const TutorialProvider = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, setUser, userId } = useContext(AuthContext);
    const isMobile = useIsMobile();

    const [state, dispatch] = useReducer(tutorialReducer, initialState, (base) => {
        // Resume a tutorial the tab was already running. A definition whose steps
        // changed under us is dropped rather than resumed into the wrong place.
        const session = readSession();
        if (!session) return base;
        const definition = registry[session.tutorialId];
        if (!definition || definition.version !== session.version) {
            clearSession();
            return base;
        }
        return {
            ...base,
            status: TUTORIAL_STATUS.ACTIVE,
            tutorialId: session.tutorialId,
            stepIndex: Math.min(session.stepIndex, definition.steps.length - 1),
            entities: session.entities || {},
        };
    });

    const [resolved, setResolved] = useState(null);
    const [resolveNonce, setResolveNonce] = useState(0);
    const [completeDismissed, setCompleteDismissed] = useState(false);

    const actionsRef = useRef({});
    const stateRef = useRef(state);
    const enteredRef = useRef(null);
    const completedRef = useRef(null);

    const tutorial = state.tutorialId ? registry[state.tutorialId] : null;
    const stepCount = tutorial?.steps.length ?? 0;

    const step = useMemo(() => {
        if (!tutorial) return null;
        const raw = tutorial.steps[state.stepIndex];
        return raw ? normalizeStep(raw, { isMobile }) : null;
    }, [tutorial, state.stepIndex, isMobile]);

    const ctx = useMemo(
        () => ({
            tutorialId: state.tutorialId,
            stepIndex: state.stepIndex,
            entities: state.entities,
            scenarioId: state.entities.scenarioId,
            userId,
            isMobile,
        }),
        [state.tutorialId, state.stepIndex, state.entities, userId, isMobile]
    );

    const ctxRef = useRef(ctx);
    useEffect(() => {
        ctxRef.current = ctx;
    }, [ctx]);
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    /** True when the browser is actually on the page this step belongs to. */
    const onStepRoute = useMemo(() => {
        if (!step) return false;
        return !!matchPath(fillRoute(step.route, state.entities), location.pathname);
    }, [step, state.entities, location.pathname]);

    const isActive = state.status === TUTORIAL_STATUS.ACTIVE && onStepRoute;

    /**
     * True while a tutorial owns the session, wherever the user happens to be.
     *
     * `isActive` also demands that the browser is on the current step's route,
     * which goes briefly false on every navigation and while a page remounts.
     * Anything that must not flicker over that gap — a canvas that would
     * re-centre itself and undo the tour's node layout — asks this instead.
     */
    const isRunning =
        state.status === TUTORIAL_STATUS.ACTIVE || state.status === TUTORIAL_STATUS.PAUSED;

    // Session persistence

    useEffect(() => {
        if (state.status === TUTORIAL_STATUS.ACTIVE || state.status === TUTORIAL_STATUS.PAUSED) {
            writeSession({
                tutorialId: state.tutorialId,
                stepIndex: state.stepIndex,
                entities: state.entities,
                version: registry[state.tutorialId]?.version,
            });
        } else {
            clearSession();
        }
    }, [state.status, state.tutorialId, state.stepIndex, state.entities]);

    // Server-side status flags

    const markStatus = useCallback(
        async (tutorialId, event) => {
            if (!userId) return;
            // Optimistic, so a slow network cannot show the prompt twice.
            setUser?.((current) =>
                current
                    ? {
                          ...current,
                          tutorials: {
                              ...(current.tutorials || {}),
                              [tutorialId]: {
                                  ...((current.tutorials || {})[tutorialId] || {}),
                                  [`${event}At`]: new Date().toISOString(),
                              },
                          },
                      }
                    : current
            );
            try {
                const updated = await updateMyTutorialStatus(tutorialId, event);
                if (updated) setUser?.(updated);
            } catch (error) {
                console.error(`Could not record tutorial "${event}":`, error);
            }
        },
        [userId, setUser]
    );

    const markPrompted = useCallback((id) => markStatus(id, 'prompted'), [markStatus]);
    const markCompleted = useCallback((id) => markStatus(id, 'completed'), [markStatus]);
    const markDismissed = useCallback((id) => markStatus(id, 'dismissed'), [markStatus]);

    const hasBeenPrompted = useCallback(
        (tutorialId) => !!user?.tutorials?.[tutorialId]?.promptedAt,
        [user]
    );

    // Lifecycle

    const start = useCallback((tutorialId, entities = {}) => {
        if (!registry[tutorialId]) {
            console.error(`Unknown tutorial: ${tutorialId}`);
            return;
        }
        enteredRef.current = null;
        completedRef.current = null;
        setResolved(null);
        setCompleteDismissed(false);
        dispatch({ type: 'START', tutorialId, entities });
    }, []);

    const exit = useCallback(() => {
        const { tutorialId } = stateRef.current;
        enteredRef.current = null;
        setResolved(null);
        dispatch({ type: 'EXIT' });
        clearSession();
        if (tutorialId) markDismissed(tutorialId);
    }, [markDismissed]);

    const advance = useCallback(() => {
        const count = registry[stateRef.current.tutorialId]?.steps.length ?? 0;
        dispatch({ type: 'ADVANCE', stepCount: count });
    }, []);

    const next = useCallback(() => {
        const current = stateRef.current;
        const definition = registry[current.tutorialId];
        const raw = definition?.steps[current.stepIndex];
        if (!raw || normalizeStep(raw).advanceOn.type !== 'next') return;
        advance();
    }, [advance]);

    const retry = useCallback(() => {
        setResolved(null);
        setResolveNonce((n) => n + 1);
        dispatch({ type: 'RESOLVE_OK' });
    }, []);

    const resume = useCallback(() => {
        const current = stateRef.current;
        const definition = registry[current.tutorialId];
        const raw = definition?.steps[current.stepIndex];
        if (!raw) return;
        navigate(fillRoute(normalizeStep(raw).route, current.entities));
    }, [navigate]);

    // Recorded on reaching the end rather than on closing the congratulations,
    // so somebody who publishes and then shuts the tab still counts as having
    // finished.
    useEffect(() => {
        if (state.status !== TUTORIAL_STATUS.FINISHED || !state.tutorialId) return;
        if (completedRef.current === state.tutorialId) return;
        completedRef.current = state.tutorialId;
        markCompleted(state.tutorialId);
    }, [state.status, state.tutorialId, markCompleted]);

    const dismissComplete = useCallback(() => {
        setCompleteDismissed(true);
        dispatch({ type: 'EXIT' });
        clearSession();
    }, []);

    // Action registry — components hand the tutorial the functions it may call

    const registerActions = useCallback((actions) => {
        const keys = Object.keys(actions || {});
        Object.assign(actionsRef.current, actions);
        return () => {
            keys.forEach((key) => {
                if (actionsRef.current[key] === actions[key]) delete actionsRef.current[key];
            });
        };
    }, []);

    // Target resolution

    const entitiesKey = JSON.stringify(state.entities);
    const stepKey = `${state.tutorialId}:${state.stepIndex}`;

    useEffect(() => {
        if (state.status !== TUTORIAL_STATUS.ACTIVE || !step || !onStepRoute) {
            setResolved(null);
            return undefined;
        }

        const selectors = step.spotlight
            .map((ref) => toSelector(ref, ctxRef.current))
            .filter((selector) => !!selector);

        if (selectors.length === 0) {
            setResolved([]);
            return undefined;
        }

        const controller = new AbortController();
        let cancelled = false;

        waitForElements(selectors, { timeoutMs: step.waitFor.timeoutMs, signal: controller.signal })
            .then((found) => {
                if (cancelled) return;
                setResolved(found);
                dispatch({ type: 'RESOLVE_OK' });
            })
            .catch((error) => {
                if (cancelled || error.name === 'AbortError') return;
                console.warn(error.message);
                dispatch({ type: 'RESOLVE_TIMEOUT' });
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
        // `resolveNonce` is what retry() bumps; nothing else re-runs resolution
        // for a step whose targets never showed up.
    }, [state.status, stepKey, entitiesKey, onStepRoute, step, resolveNonce]);

    const { rects, elements, anchorVersion } = useSpotlightRects(resolved, isActive);

    const anchorEl = useMemo(() => {
        if (!step || !elements.length) return null;
        return elements[Math.min(step.anchor, elements.length - 1)] || null;
        // anchorVersion changes when an element is swapped out under us.
    }, [step, elements, anchorVersion]);

    // Bring the anchor into view once per step; the settings page is taller
    // than the viewport and the tooltip is useless off-screen.
    useEffect(() => {
        if (!isActive || !anchorEl) return;
        const rect = anchorEl.getBoundingClientRect();
        const offscreen = rect.top < 0 || rect.bottom > window.innerHeight;
        if (offscreen && typeof anchorEl.scrollIntoView === 'function') {
            anchorEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [isActive, stepKey]);

    // onEnter — re-fires on resume, because whatever it set up (an open panel)
    // was probably torn down while we were away.

    const enterKey = `${stepKey}:${state.status}:${onStepRoute}`;
    useEffect(() => {
        if (state.status !== TUTORIAL_STATUS.ACTIVE || !onStepRoute || !step) return;
        if (enteredRef.current === enterKey) return;
        enteredRef.current = enterKey;
        try {
            step.onEnter?.(ctxRef.current, actionsRef.current);
        } catch (error) {
            console.error(`Tutorial step "${step.id}" onEnter threw:`, error);
        }
    }, [enterKey, state.status, onStepRoute, step]);

    // The click shield

    const policyRef = useRef({ active: false, allow: [], deny: [], extraEvents: [] });
    policyRef.current = useMemo(() => {
        if (!isActive || !step) {
            return { active: false, allow: [], deny: [], extraEvents: [] };
        }
        const allow = step.allow.map((ref) => toSelector(ref, ctx)).filter(Boolean);
        const advanceSelector =
            step.advanceOn.type === 'click' ? toSelector(step.advanceOn.target, ctx) : null;

        return {
            active: true,
            // The advance target is always clickable, even if the author forgot
            // to list it among the spotlights.
            allow: advanceSelector && !allow.includes(advanceSelector) ? [...allow, advanceSelector] : allow,
            deny: step.deny,
            extraEvents: step.block,
            onAllowedClick: (target) => {
                if (!advanceSelector) return;
                try {
                    if (target.closest(advanceSelector)) advance();
                } catch (error) {
                    console.error('Tutorial advance selector is invalid:', advanceSelector, error);
                }
            },
        };
    }, [isActive, step, ctx, advance]);

    useEffect(() => installShield({ getPolicy: () => policyRef.current }), []);

    // Bus events

    useEffect(
        () =>
            tutorialBus.onAny((name, payload) => {
                const current = stateRef.current;
                if (current.status !== TUTORIAL_STATUS.ACTIVE) return undefined;

                const definition = registry[current.tutorialId];
                const raw = definition?.steps[current.stepIndex];
                if (!raw) return undefined;
                const active = normalizeStep(raw, { isMobile: ctxRef.current.isMobile });
                const advanceOn = active.advanceOn;
                const actions = actionsRef.current;

                if (advanceOn.type !== 'event' || advanceOn.name !== name) {
                    // Not what we are waiting for. A step can still have
                    // something to say about it — a failed publish, say.
                    const hint = active.hintOn?.[name];
                    if (hint) {
                        dispatch({
                            type: 'SET_HINT',
                            hint: typeof hint === 'function' ? hint(payload, ctxRef.current) : hint,
                        });
                    }
                    return undefined;
                }

                let matches = true;
                try {
                    matches = advanceOn.when ? !!advanceOn.when(payload, ctxRef.current) : true;
                } catch (error) {
                    console.error(`Tutorial step "${active.id}" when() threw:`, error);
                    matches = false;
                }

                if (!matches) {
                    try {
                        const hint = active.invalid?.handle?.(payload, ctxRef.current, actions);
                        if (hint) dispatch({ type: 'SET_HINT', hint });
                    } catch (error) {
                        console.error(`Tutorial step "${active.id}" invalid.handle threw:`, error);
                    }
                    return undefined;
                }

                let entities = current.entities;
                if (advanceOn.capture) {
                    const value = payload?.[advanceOn.capture] ?? payload?.id;
                    entities = { ...entities, [advanceOn.capture]: value };
                    dispatch({ type: 'SET_ENTITY', key: advanceOn.capture, value });
                }

                let result;
                try {
                    result = active.onEvent?.(payload, { ...ctxRef.current, entities }, actions);
                } catch (error) {
                    console.error(`Tutorial step "${active.id}" onEvent threw:`, error);
                }

                dispatch({ type: 'ADVANCE', stepCount: definition.steps.length });
                // Returned so `emitAsync` callers wait on the pre-fill.
                return result;
            }),
        []
    );

    // Route watching: advance, pause, resume

    useEffect(() => {
        if (state.status !== TUTORIAL_STATUS.ACTIVE && state.status !== TUTORIAL_STATUS.PAUSED) {
            return undefined;
        }
        if (!step) return undefined;

        if (state.status === TUTORIAL_STATUS.ACTIVE && step.advanceOn.type === 'route') {
            if (matchPath(fillRoute(step.advanceOn.path, state.entities), location.pathname)) {
                advance();
                return undefined;
            }
        }

        if (state.status === TUTORIAL_STATUS.ACTIVE && !onStepRoute) {
            // Give a transitional navigation a moment to land before stepping aside.
            const timer = setTimeout(() => dispatch({ type: 'PAUSE' }), PAUSE_DEBOUNCE_MS);
            return () => clearTimeout(timer);
        }

        if (state.status === TUTORIAL_STATUS.PAUSED && onStepRoute) {
            dispatch({ type: 'RESUME' });
        }

        return undefined;
    }, [location.pathname, state.status, state.entities, step, onStepRoute, advance]);

    // Locking

    const isLocked = useCallback(
        (key) => {
            if (!isActive || !tutorial) return false;
            const locks = tutorial.locks || [];
            return locks.some(
                (lock) => lock === '*' || lock === key || key.startsWith(`${lock}.`)
            );
        },
        [isActive, tutorial]
    );

    const value = useMemo(
        () => ({
            status: state.status,
            isRunning,
            isActive,
            isPaused: state.status === TUTORIAL_STATUS.PAUSED,
            isFinished: state.status === TUTORIAL_STATUS.FINISHED && !completeDismissed,
            tutorial,
            step,
            stepIndex: state.stepIndex,
            stepCount,
            entities: state.entities,
            hint: state.hint,
            resolveError: state.resolveError,
            rects,
            anchorEl,
            hasBeenPrompted,
            start,
            exit,
            next,
            retry,
            resume,
            dismissComplete,
            isLocked,
            registerActions,
            markPrompted,
            markCompleted,
            markDismissed,
            ctx,
        }),
        [
            state.status,
            state.stepIndex,
            state.entities,
            state.hint,
            state.resolveError,
            isRunning,
            isActive,
            completeDismissed,
            tutorial,
            step,
            stepCount,
            rects,
            anchorEl,
            hasBeenPrompted,
            start,
            exit,
            next,
            retry,
            resume,
            dismissComplete,
            isLocked,
            registerActions,
            markPrompted,
            markCompleted,
            markDismissed,
            ctx,
        ]
    );

    return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export const useTutorial = () => useContext(TutorialContext);

/**
 * Whether a given area of the UI should be inert right now.
 * @param {string} key a lock scope such as 'titlePage' or 'editor.text'
 */
export const useTutorialLock = (key) => useTutorial().isLocked(key);

export default TutorialContext;
